import { useQueries, useQuery } from '@tanstack/react-query'
import { listOrders } from '@/api/orders'
import { getBilling } from '@/api/billing'
import type { Order } from '@/types/order'
import type { OrderStatus } from '@/types/enums'
import type { CalculateBillingResult } from '@/types/billing'
import type { BillingFilterState } from '@/components/billing/BillingFilters'

/**
 * Жёсткий лимит выборки. Агрегация метрик идёт по клиенту через N
 * параллельных вызовов `calculate-billing`. Если фильтр возвращает
 * больше — UI рисует баннер «показано X из Y, уточните фильтры».
 * См. `TARIFFS_SPEC.md` §9.1.
 */
export const BILLING_HARD_LIMIT = 250

const TERMINAL_STATUSES: OrderStatus[] = [
  'COMPLETED',
  'REFUSED_BY_INSPECTOR',
  'REFUSED_BY_SELLER',
  'REFUSED_BUYOUT',
  'CANCELLED',
]

function expandOutcome(
  outcome: BillingFilterState['outcome'],
): OrderStatus[] {
  if (outcome === 'all_terminal') return TERMINAL_STATUSES
  return [outcome]
}

export interface BillingRow {
  order: Order
  billing: CalculateBillingResult | null
  /** true, пока соответствующий `calculate-billing` ещё не завершил запрос */
  pending: boolean
  /** Сообщение об ошибке, если `calculate-billing` упал на этом заказе */
  error: string | null
}

export interface BillingMetrics {
  revenue: number
  payout: number
  margin: number
  ordersCount: number
  settledCount: number
  /** true, если всё уже посчитано (нет pending-запросов и нет ошибок) */
  complete: boolean
}

export interface UseBillingAggregationResult {
  rows: BillingRow[]
  metrics: BillingMetrics
  /** Сколько всего заказов матчит фильтр в БД (включая не загруженные) */
  total: number
  /** Загружаем первичный список заказов */
  isLoadingOrders: boolean
  /** Хотя бы один `calculate-billing` ещё выполняется */
  isLoadingBilling: boolean
  /** Фильтр вернул больше `BILLING_HARD_LIMIT` заказов */
  partial: boolean
  /** Ошибка получения списка заказов */
  ordersError: Error | null
}

export function useBillingAggregation(
  filters: BillingFilterState,
): UseBillingAggregationResult {
  const listQuery = useQuery({
    queryKey: ['billing-list', filters],
    queryFn: () =>
      listOrders({
        status: expandOutcome(filters.outcome),
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        dateField: 'status_updated_at',
        companyId: filters.companyId || undefined,
        inspectorId: filters.inspectorId || undefined,
        city: filters.city || undefined,
        page: 1,
        pageSize: BILLING_HARD_LIMIT,
      }),
    staleTime: 30_000,
  })

  const orders = listQuery.data?.items ?? []
  const total = listQuery.data?.total ?? 0

  const billingQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: ['billing', o.id] as const,
      queryFn: () => getBilling(o.id),
      staleTime: 30_000,
    })),
  })

  const rows: BillingRow[] = orders.map((order, i) => {
    const q = billingQueries[i]
    return {
      order,
      billing: (q?.data as CalculateBillingResult | undefined) ?? null,
      pending: !!q && q.isPending,
      error: q?.error ? String((q.error as Error).message ?? q.error) : null,
    }
  })

  let revenue = 0
  let payout = 0
  let settledCount = 0
  for (const row of rows) {
    if (!row.billing) continue
    settledCount += 1
    revenue += row.billing.totals.total_customer
    payout += row.billing.totals.total_inspector
  }

  const isLoadingBilling = billingQueries.some((q) => q.isPending)

  return {
    rows,
    metrics: {
      revenue,
      payout,
      margin: revenue - payout,
      ordersCount: orders.length,
      settledCount,
      complete: !isLoadingBilling && settledCount === orders.length,
    },
    total,
    isLoadingOrders: listQuery.isPending,
    isLoadingBilling,
    partial: total > orders.length,
    ordersError:
      listQuery.error instanceof Error ? listQuery.error : null,
  }
}
